import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { Device, from, until } from '@helpers/media';
import { m } from 'framer-motion';
import styled from 'styled-components';

const Container = styled.div`
  position: relative;
  width: 100%;
  border-radius: 4px;
  background-color: ${brand.white};
  display: flex;
  flex-direction: row;
  outline: 2px solid transparent;
  transition: outline-color 0.3s linear;

  &[aria-current='true'] {
    outline-color: ${brand.green.light};
  }

  [data-device='mobile'] & {
    outline: none;
  }

  [data-button='true'] & {
    pointer-events: none !important;

    * {
      pointer-events: none !important;
    }
  }
`;

const SearchIcon = styled.span`
  display: inline-flex;
  place-content: center;
  place-items: center;
  width: 36px;
  height: 100%;
  color: ${brand.grey[45]};
  pointer-events: none;
  opacity: 1;
  transition: opacity 0.3s linear;

  [aria-current='true'] & {
    opacity: 0;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const MicIcon = styled.button`
  position: absolute;
  top: 0;
  right: 48px;
  display: inline-flex;
  place-content: center;
  place-items: center;
  width: 36px;
  height: 100%;
  border: none;
  color: ${brand.grey[45]};
  background-color: ${brand.white};
  cursor: pointer;
  transition: opacity 0.3s linear;

  @media ${from(Device.DesktopSmall)} {
    right: 6px;

    [aria-current='true'] & {
      right: 48px;
    }
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 40px;
  overflow: hidden;
`;

const InputPlaceholder = styled(m.span)`
  position: absolute;
  top: 50%;
  margin-top: -7px;
  white-space: nowrap;
  pointer-events: none;
  color: ${brand.grey[35]};
  padding-left: 18px;
  font-size: ${fluid(12, 14)};
  overflow: hidden;
`;

const Input = styled.input`
  display: block;
  width: 100%;
  height: 40px;
  padding: 0 18px 0 18px;
  ${fonts.helvetica['63-medium-extended']}
  font-size: 16px;
  color: ${brand.black};
  border-radius: 4px;
  background-color: ${brand.white};
  border: none;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  transition: outline-color 0.3s linear, padding 0.3s linear;

  @media ${from(Device.DesktopSmall)} {
    font-size: ${fluid(12, 14, Device.DesktopSmall)};
    padding: 0 8px 0 18px;
    transition: padding 0.3s linear;
  }

  &::placeholder {
    ${fonts.helvetica['53-extended']}
    color: ${brand.grey[50]};
    font-size: 16px;

    [data-device='mobile'] & {
      font-size: 12px;
      transform: translateY(-2px);
    }

    [data-animated-placeholder='true'] & {
      text-indent: -9999px;
    }

    @media ${from(Device.DesktopSmall)} {
      font-size: ${fluid(12, 14, Device.DesktopSmall)};
      opacity: 0;

      [data-animated-placeholder='false'] & {
        opacity: 1;
      }
    }
  }

  &[aria-current='true'] {
    @media ${from(Device.DesktopSmall)} {
      &[data-has-mic='true'] {
        padding: 0 34px 0 18px;
      }

      &[data-has-mic='false'] {
        padding: 0 18px 0 18px;
      }
    }
  }
`;

const Button = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  place-content: center;
  place-items: center;
  /* position: absolute; */
  top: 0;
  right: 0;
  width: 48px;
  flex: 0 0 auto;
  height: 40px;
  background-color: ${brand.grey[90]};
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  appearance: none;
  border: none;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s linear;

  [aria-current='false'] & {
    width: 0px;
    height: 0px;
  }

  [aria-current='true'] & {
    opacity: 1;
    pointer-events: auto;
  }

  svg {
    width: 18px;
    height: 18px;

    @media ${until(Device.DesktopSmall)} {
      width: 20px;
      height: 20px;
    }
  }
`;

const Dropdown = styled.div`
  display: inline-flex;
  place-items: center;
  background-color: ${brand.grey[90]};
  padding: 0 42px 0 16px;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  ${fonts.helvetica['55-roman']}
  font-size: ${fluid(12, 14)};
  margin-top: 1px;
  position: relative;
  line-height: 1.1;
  white-space: nowrap;
  cursor: pointer;

  > svg {
    width: 16px;
    height: 8px;
    position: absolute;
    right: 13px;
    top: 50%;
    transform: translateY(-50%);
  }

  &[data-is-open='true'] {
    > svg {
      transform: translateY(-50%) rotate(180deg);
    }
  }
`;

const DropdownSelected = styled.div`
  [data-max-width='true'] & {
    max-width: ${fluid(70, 150)};
    text-overflow: ellipsis;
    overflow: hidden;
  }

  @media ${until(Device.TabletLarge)} {
    max-width: ${fluid(70, 130)};
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

const DropdownItems = styled.div`
  background-color: ${brand.grey[90]};
  position: absolute;
  top: calc(100% + 6px);
  left: 0px;
  padding-bottom: 10px;
  border-radius: 4px;
  width: 220px;
`;

const DropdownItem = styled.button.attrs({ type: 'button' })`
  ${ButtonReset};
  ${fonts.helvetica['55-roman']};
  display: block;
  width: 100%;
  text-align: left;
  padding: 7px 16px 7px 16px;
  cursor: pointer;
  white-space: break-spaces;

  &[aria-selected='true'] {
    text-decoration: underline;
  }

  &:hover {
    background-color: ${brand.grey[80]};
  }
`;

const SearchInputStyles = {
  Container,
  SearchIcon,
  MicIcon,
  Input,
  InputWrapper,
  InputPlaceholder,
  Button,
  Dropdown,
  DropdownSelected,
  DropdownItems,
  DropdownItem,
};

export default SearchInputStyles;
